import {Pill, PillVariant} from '@forecast-it/design-system';
import React from 'react';
import styled from 'styled-components';

export interface NovaLabelChipProps {
	/**
	 * Which side of the conversation this label belongs to. Nova's own agent name is an
	 * information pill, the person's reply tag a neutral one - the same distinction the two cards
	 * either side of them already draw.
	 */
	tone: 'nova' | 'user';
	children: React.ReactNode;
	'data-cy'?: string;
}

/**
 * One short label naming who is speaking, used on both sides of the conversation: the agent's name
 * on Nova's card, the reply tag on the person's.
 *
 * This was a token-built span until the thread moved onto the design system. The note it carried
 * then said Pill's `information` variant sat in a different blue from everything around it - true
 * of the cards it used to sit on, which were drawn from raw tokens too. Those are a design-system
 * Frame and ColouredContainer now, so the pill and its surroundings come from the same palette and
 * the mismatch it was avoiding is gone.
 */
// Two jobs, both forced by the same gap in Pill.
//
// It renders only the props it knows and drops the rest, so a test id passed to it never reaches
// the DOM - the slot carries it instead, as this feature's wrappers do around Frame and
// ColouredContainer.
//
// And it ships one size: subheading-2 type inside 4px/12px of padding, which stands about 28px
// tall. That is a pill for a page header, not a label sitting inside a line of thread header
// beside a 12px name and timestamp, where it set the height of the whole row. The compact fit
// below is the size the design draws it at. Raised with the design-system team alongside the
// missing compact button size.
const CHIP_HEIGHT = 20;

const ChipSlot = styled.span`
	display: inline-flex;
	min-width: 0;

	> * {
		height: ${CHIP_HEIGHT}px;
		min-height: ${CHIP_HEIGHT}px;
		padding: 0 6px;
		box-sizing: border-box;
	}

	/* The label inside carries Pill's own type, which has to come down with the box around it. */
	> * * {
		font-size: 11px;
		line-height: 16px;
	}
`;

export const NovaLabelChip = (props: NovaLabelChipProps): JSX.Element => (
	<ChipSlot data-cy={props['data-cy']}>
		<Pill variant={props.tone === 'nova' ? PillVariant.INFORMATION : PillVariant.NEUTRAL}>{props.children}</Pill>
	</ChipSlot>
);
